import { Component, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';

import { PartService } from '../../../core/services/part.service';
import { Part } from '../../../core/models/part.model';

@Component({
  selector: 'app-part-list',
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './part-list.html',
  styleUrl: './part-list.scss',
})
export class PartList {
  private readonly partService = inject(PartService);
  private readonly fb = inject(FormBuilder);

  protected readonly parts = signal<Part[]>([]);
  protected readonly loading = signal(true);
  protected readonly error = signal<string | null>(null);

  protected readonly page = signal(1);
  protected readonly pageSize = signal(25);
  protected readonly totalCount = signal(0);
  protected readonly overallTotalCount = signal(0);
  protected readonly totalPages = computed(() => Math.max(1, Math.ceil(this.totalCount() / this.pageSize())));

  protected readonly searchTerm = signal('');
  protected readonly categoryFilter = signal('');
  private searchDebounceHandle?: ReturnType<typeof setTimeout>;

  protected readonly categoryOptions = ['TreadSpec X Ramp Parts', 'TreadSpec X Cables', 'TreadSpec - 3.0 - Camera - LPR'];

  protected readonly hasActiveFilters = computed(() => !!this.searchTerm() || !!this.categoryFilter());

  protected readonly modalOpen = signal(false);
  protected readonly saving = signal(false);
  protected readonly editingPart = signal<Part | null>(null);

  protected readonly partForm = this.fb.nonNullable.group({
    partNumber: [''],
    description: ['', Validators.required],
    category: [''],
    unitPrice: [''],
  });

  constructor() {
    this.loadParts();
    this.loadOverallTotal();
  }

  private loadParts(): void {
    this.loading.set(true);
    this.error.set(null);
    this.partService.getParts({
      page: this.page(),
      pageSize: this.pageSize(),
      search: this.searchTerm() || undefined,
      category: this.categoryFilter() || undefined,
    }).subscribe({
      next: (result) => {
        this.parts.set(result.items);
        this.totalCount.set(result.totalCount);
        this.loading.set(false);
      },
      error: (err) => {
        this.error.set('Failed to load parts. Is the FieldService API running?');
        this.loading.set(false);
        console.error(err);
      }
    });
  }

  private loadOverallTotal(): void {
    this.partService.getParts({ page: 1, pageSize: 1 }).subscribe({
      next: (result) => this.overallTotalCount.set(result.totalCount),
      error: (err) => console.error(err)
    });
  }

  protected onSearchInput(value: string): void {
    this.searchTerm.set(value);
    clearTimeout(this.searchDebounceHandle);
    this.searchDebounceHandle = setTimeout(() => {
      this.page.set(1);
      this.loadParts();
    }, 400);
  }

  protected onCategoryFilterChange(value: string): void {
    this.categoryFilter.set(value);
    this.page.set(1);
    this.loadParts();
  }

  protected clearFilters(): void {
    clearTimeout(this.searchDebounceHandle);
    this.searchTerm.set('');
    this.categoryFilter.set('');
    this.page.set(1);
    this.loadParts();
  }

  protected goToPreviousPage(): void {
    if (this.page() > 1) {
      this.page.update((p) => p - 1);
      this.loadParts();
    }
  }

  protected goToNextPage(): void {
    if (this.page() < this.totalPages()) {
      this.page.update((p) => p + 1);
      this.loadParts();
    }
  }

  protected openAddModal(): void {
    this.editingPart.set(null);
    this.partForm.reset();
    this.modalOpen.set(true);
  }

  protected openEditModal(part: Part): void {
    this.editingPart.set(part);
    this.partForm.reset({
      partNumber: part.partNumber ?? '',
      description: part.description ?? '',
      category: part.category ?? '',
      unitPrice: part.unitPrice != null ? String(part.unitPrice) : '',
    });
    this.modalOpen.set(true);
  }

  protected closeModal(): void {
    this.modalOpen.set(false);
  }

  protected savePart(): void {
    if (this.partForm.invalid) {
      this.partForm.markAllAsTouched();
      return;
    }

    const value = this.partForm.getRawValue();
    const editing = this.editingPart();
    this.saving.set(true);

    const request = {
      ...value,
      unitPrice: value.unitPrice ? Number(value.unitPrice) : undefined,
    };

    if (editing != null) {
      this.partService.updatePart(editing.partId, { ...request, isActive: editing.isActive ?? true }).subscribe({
        next: (updated) => {
          this.parts.update((list) => list.map((p) => (p.partId === updated.partId ? updated : p)));
          this.saving.set(false);
          this.closeModal();
        },
        error: (err) => {
          this.saving.set(false);
          this.error.set('Failed to save part.');
          console.error(err);
        }
      });
    } else {
      this.partService.createPart(request).subscribe({
        next: (created) => {
          this.parts.update((list) => [...list, created]);
          this.overallTotalCount.update((c) => c + 1);
          this.saving.set(false);
          this.closeModal();
        },
        error: (err) => {
          this.saving.set(false);
          this.error.set('Failed to create part.');
          console.error(err);
        }
      });
    }
  }

  protected deactivatePart(part: Part): void {
    this.partService.deactivatePart(part.partId).subscribe({
      next: () => {
        this.parts.update((list) => list.map((p) => (p.partId === part.partId ? { ...p, isActive: false } : p)));
      },
      error: (err) => {
        this.error.set('Failed to deactivate part.');
        console.error(err);
      }
    });
  }

  protected categoryColor(category: string | null): string {
    switch (category) {
      case 'TreadSpec X Ramp Parts': return '#8DC63F';
      case 'TreadSpec X Cables': return '#00B9F2';
      case 'TreadSpec - 3.0 - Camera - LPR': return '#F29727';
      default: return '#64748B';
    }
  }
}
